import MapboxDraw from "@mapbox/mapbox-gl-draw";
import {
  CircleMode,
  DirectModeOverride,
  DragCircleMode,
  DrawRectangleMode,
  SimpleSelectMode,
  SnapLineMode,
  SnapModeDrawStyles,
  SnapPointMode,
  SnapPolygonMode,
} from "Components/Geography/MapBox/lib";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import mapboxgl from "mapbox-gl";
import CustomControlForLayersListing from "Components/Geography/MapBox/features/CustomControlForLayersListing";
// import customControlForSnapping from "Components/Geography/MapBox/features/CustomControlForSnapping";
import {
  setDrawReference,
  setDrawShapeGeometry,
  setIsShapeDrawingCompleted,
  setMap,
} from "store/surveyJS/reducer.actions";
import { __paintShapeByGeoJsonOnMap__ } from "../workerMehods";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { I_InitialStateOfSurveyJS } from "store/surveyJS/initialState";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? "";

const useInitializeMap = (mapContainer: any, setZoom: any) => {
  const map = useRef<any>(null);
  const [drawRef, setDrawRef] = useState<any>(null);
  const [isMapFUllyLoaded, setIsMapFullyLoaded] = useState(false);

  const { geography } = useSelector<RootState, I_InitialStateOfSurveyJS>(
    (state) => state.surveyJS
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (map.current) return; // initialize map only once

    (async () => {
      if (!mapContainer.current) return;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: process.env.REACT_APP_MAPBOX_STYLE_URL ?? "",
        center: [-156.44636531857395, 62.40265300551266],
        zoom: 2,
        pitch: 0,
        bearing: 0,
        renderWorldCopies: false,
        minZoom: 0.999,
        // @ts-ignore
        projection: {
          name: "mercator",
        },
      });

      dispatch(setMap(map.current));

      // adding zoom and navigation controls
      const nav = new mapboxgl.NavigationControl();
      map.current.addControl(nav, "bottom-right");

      // adding full screen control
      map.current.addControl(new mapboxgl.FullscreenControl());

      // adding custom control for layers listing
      const customLayerListingControl = new CustomControlForLayersListing();
      map.current.addControl(customLayerListingControl, "top-right");

      // adding custom control for snapping
      // const customSnappingControl = new customControlForSnapping();
      // map.current.addControl(customSnappingControl);
      
      mapContainer.current.style.width = "100%";
      // initializing all controls of drawing shapes
      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          trash: true,
        },
        modes: {
          ...MapboxDraw.modes,
          draw_point: SnapPointMode,
          // draw_polygon: SnapPolygonMode,
          draw_circle: CircleMode,
          direct_select: DirectModeOverride,
          drag_circle: DragCircleMode,
          simple_select: SimpleSelectMode,
          draw_line_string: SnapLineMode,
          draw_rectangle: DrawRectangleMode,
        },
        // Styling guides
         // styles: SnapModeDrawStyles,
        styles: [
          // ACTIVE (being drawn)
          // line stroke

          {
            id: "gl-draw-line",
            type: "line",
            filter: ["all", ["==", "$type", "LineString"]],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": "#D20C0C",
              "line-dasharray": [0.2, 2],
              "line-width": 2,
            },
          },
          // polygon fill
          {
            id: "gl-draw-polygon-fill",
            type: "fill",
            filter: ["all", ["==", "$type", "Polygon"]],
            paint: {
              "fill-color": "#D20C0C",
              "fill-outline-color": "#D20C0C",
              "fill-opacity": 0.2,
            },
          },
          // polygon mid points
          {
            id: "gl-draw-polygon-midpoint",
            type: "circle",
            filter: [
              "all",
              ["==", "$type", "Point"],
              ["==", "meta", "midpoint"],
            ],
            paint: {
              "circle-radius": 3,
              "circle-color": "#fbb03b",
            },
          },
          // polygon outline stroke
          // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
          {
            id: "gl-draw-polygon-stroke-active",
            type: "line",
            filter: ["all", ["==", "$type", "Polygon"]],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": "#D20C0C",
              "line-dasharray": [0.2, 2],
              "line-width": 1,
            },
          },
          // vertex point halos
          {
            id: "gl-draw-polygon-and-line-vertex-halo-active",
            type: "circle",
            filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
            paint: {
              "circle-radius": 5,
              "circle-color": "#FFF",
            },
          },
          // vertex points
          {
            id: "gl-draw-polygon-and-line-vertex-active",
            type: "circle",
            filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"]],
            paint: {
              "circle-radius": 3,
              "circle-color": "#D20C0C",
            },
          },
          {
            id: "gl-draw-line-inactive",
            type: "line",
            filter: [
              "all",
              ["==", "$type", "LineString"],
              ["!=", "active", "true"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": "#007FFF", // Blue color for inactive
              "line-dasharray": [0.2, 2],
              "line-width": 2,
            },
          },
          // Polygon fill when inactive
          {
            id: "gl-draw-polygon-fill-inactive",
            type: "fill",
            filter: [
              "all",
              ["==", "$type", "Polygon"],
              ["!=", "active", "true"],
            ],
            paint: {
              "fill-color": "#007FFF", // Blue for inactive polygons
              "fill-outline-color": "#007FFF",
              "fill-opacity": 0.2, // Adjust opacity as needed
            },
          },
          // Polygon outline stroke when inactive
          {
            id: "gl-draw-polygon-stroke-inactive",
            type: "line",
            filter: [
              "all",
              ["==", "$type", "Polygon"],
              ["!=", "active", "true"],
            ],
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
            paint: {
              "line-color": "#007FFF",
              // "line-dasharray": [0.2, 2],
              "line-width": 2,
            },
          },
          // Vertex points when inactive
          {
            id: "gl-draw-polygon-and-line-vertex-inactive",
            type: "circle",
            filter: [
              "all",
              ["==", "meta", "vertex"],
              ["==", "$type", "Point"],
              ["!=", "active", "true"],
            ],
            paint: {
              "circle-radius": 3,
              "circle-color": "#007FFF",
            },
          },
        ],
        userProperties: true,
        // Config snapping features
        // @ts-ignore
        snap: false,
        snapOptions: {
          snapPx: 15, // defaults to 15
          snapToMidPoints: true, // defaults to false
          snapVertexPriorityDistance: 0.0025, // defaults to 1.25
        },
        guides: false,
      });

      dispatch(setDrawReference(draw));
      setDrawRef(draw);

      map.current.addControl(draw);

      toggleDeleteControl(geography.activeAreaSelectMode === "DRAW")
      // adding custom delete control for removing drawing shapes
      // const customDeleteControl = new DeleteControl("formAreaDelete");

      // map.current.addControl(customDeleteControl, "top-right");

      map.current.on("load", () => setIsMapFullyLoaded(true));
      map.current.on("draw.create", (e:any)=>{
        dispatch(setIsShapeDrawingCompleted(true));
        updateGeoJsonData()
      });
      map.current.on("draw.delete", updateGeoJsonData);
      map.current.on("draw.update", updateGeoJsonData);

      map.current.on("move", () => {
        setZoom(map.current.getZoom().toFixed(2));
      });

      // setting up and updating geojson
      // function updateGeoJsonData() {
      //   const data = draw.getAll();

      //   if (data.features.length > 0) {
      //     // removing unnecessary shape items added by map
      //     const features = data.features.filter((f: any) => {
      //       if (
      //         f.id !== "VERTICAL_GUIDE" &&
      //         f.id !== "HORIZONTAL_GUIDE" &&
      //         !f.id.includes("feature")
      //       ) {
      //         return true;
      //       }
      //       return false;
      //     });

      //     dispatch(setDrawShapeGeometry(features));
      //   }
      // }
      function updateGeoJsonData() {

        const data = draw.getAll();
        if (data.features.length > 0) {
          // removing unnecessary shape items added by map
          const features = data.features.filter((f: any) => {
            if (
              f.id !== "VERTICAL_GUIDE" &&
              f.id !== "HORIZONTAL_GUIDE" &&
              !f.id.includes("feature")
            ) {
              return true;
            }

            return false;
          });

          dispatch(setDrawShapeGeometry(features));
        } else {
          dispatch(setDrawShapeGeometry(null));
        }
      }
    })();
  }, []);

  useEffect(()=>{
    if(document && isMapFUllyLoaded){
      toggleDeleteControl(geography.activeAreaSelectMode === "DRAW")
    }

  },[isMapFUllyLoaded,geography])
  

  return { map, drawRef, isMapFUllyLoaded };
};

export { useInitializeMap };



function toggleDeleteControl(doShow:boolean){
  const trashControls = document.querySelectorAll(
    "button.mapbox-gl-draw_trash"
  ) as NodeList;

  if(trashControls && trashControls.length > 0){
    const trashControl = trashControls[0] as HTMLButtonElement;

    trashControl.style.display = doShow ? "block" : "none";
  }

  return doShow ? "Showed" : "Hided";
}